import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { c } from "react-compiler-runtime";
import { createCompatibilityActors, isMaybePreviewIframe, isMaybePreviewWindow } from "@sanity/presentation-comlink";
import { useEffect, createContext, useContext, useState, memo, useRef, isValidElement, useSyncExternalStore, useReducer } from "react";
import { createPortal } from "react-dom";
import { createEditUrl, studioPath, getDraftId, getPublishedId } from "@sanity/client/csm";
import { MenuGroup, MenuDivider, MenuItem, Box, Text, Spinner, Stack, Flex, Menu, Popover, Card, usePrefersDark, useTheme_v2, Hotkeys, LayerProvider, PortalProvider, ThemeProvider, studioTheme, isHTMLAnchorElement, isHTMLElement } from "@sanity/ui/_visual-editing";
import { styled } from "styled-components";
import { useDocuments, getArrayItemKeyAndParentPath, get, getArrayMovePatches, getArrayInsertPatches, getArrayDuplicatePatches, getArrayRemovePatches, useOptimisticActorReady, useOptimisticActor } from "../_chunks-es/mutations.js";
import { SharedStateContext, createOverlayController, sanityNodesExistInSameArray } from "../_chunks-es/SharedStateContext.js";
import { useOptimistic } from "../_chunks-es/SharedStateContext.js";
import { at, remove, insert } from "@sanity/mutate";
import { getNodeIcon, InsertMenu, PopoverPortal, useTelemetry, PointerEvents, TelemetryContext, events } from "../_chunks-es/PointerEvents.js";
import { InsertAboveIcon, InsertBelowIcon, CopyIcon, RemoveIcon, SortIcon, PublishIcon, ArrowUpIcon, ArrowDownIcon, UnpublishIcon, DocumentIcon, DragHandleIcon, ExpandIcon } from "@sanity/icons";
import { pathToUrlString } from "@sanity/visual-editing-csm";
import { createDataAttribute } from "@sanity/visual-editing-csm";
import scrollIntoView from "scroll-into-view-if-needed";
import { createNode, createNodeMachine } from "@sanity/comlink";
import { createActor } from "xstate";
import { setActor } from "../_chunks-es/context.js";
import { emptyActor } from "../_chunks-es/context.js";
import { ReplaySubject, Subject, merge } from "rxjs";
import { createDatasetMutator } from "../optimistic/index.js";
import { createDocumentMutator } from "../optimistic/index.js";
const History = (props) => {
  const $ = c(8), {
    comlink,
    history
  } = props;
  let t0, t1;
  $[0] !== comlink || $[1] !== history ? (t0 = () => comlink?.on("presentation/navigate", (data) => {
    history?.update(data);
  }), t1 = [comlink, history], $[0] = comlink, $[1] = history, $[2] = t0, $[3] = t1) : (t0 = $[2], t1 = $[3]), useEffect(t0, t1);
  let t2, t3;
  return $[4] !== comlink || $[5] !== history ? (t2 = () => {
    if (history)
      return history.subscribe((update) => {
        update.title = update.title || document.title, comlink?.post("visual-editing/navigate", update);
      });
  }, t3 = [comlink, history], $[4] = comlink, $[5] = history, $[6] = t2, $[7] = t3) : (t2 = $[6], t3 = $[7]), useEffect(t2, t3), null;
}, Meta = (props) => {
  const $ = c(3), {
    comlink
  } = props;
  let t0, t1;
  return $[0] !== comlink ? (t0 = () => {
    const sendMeta = () => {
      comlink.post("visual-editing/meta", {
        title: document.title
      });
    }, observer = new MutationObserver((t2) => {
      const [mutation] = t2;
      mutation.target.nodeName === "TITLE" && sendMeta();
    });
    return observer.observe(document.head, {
      subtree: !0,
      characterData: !0,
      childList: !0
    }), sendMeta(), () => observer.disconnect();
  }, t1 = [comlink], $[0] = comlink, $[1] = t0, $[2] = t1) : (t0 = $[1], t1 = $[2]), useEffect(t0, t1), null;
};
function getReferenceNodeAndInsertPosition(position) {
  if (position) {
    const {
      top,
      right,
      bottom,
      left
    } = position;
    if (left || top)
      return {
        node: (left ?? top).sanity,
        position: "after"
      };
    if (right || bottom)
      return {
        node: (right ?? bottom).sanity,
        position: "before"
      };
  }
}
function useDragEndEvents() {
  const $ = c(4), {
    getDocument
  } = useDocuments();
  let t0, t1;
  $[0] !== getDocument ? (t0 = () => {
    const handler = (e) => {
      const {
        insertPosition,
        target,
        preventInsertDefault
      } = e.detail;
      if (preventInsertDefault)
        return;
      const reference = getReferenceNodeAndInsertPosition(insertPosition);
      if (reference) {
        const doc = getDocument(target.id), {
          node,
          position
        } = reference, {
          key: targetKey,
          hasExplicitKey
        } = getArrayItemKeyAndParentPath(target), {
          path: arrayPath,
          key: referenceItemKey
        } = getArrayItemKeyAndParentPath(node);
        arrayPath && referenceItemKey && referenceItemKey !== targetKey && doc.patch(async (t22) => {
          const {
            getSnapshot
          } = t22, snapshot = await getSnapshot(), elementValue = get(snapshot, target.path);
          return hasExplicitKey ? [at(arrayPath, remove({
            _key: targetKey
          })), at(arrayPath, insert(elementValue, position, {
            _key: referenceItemKey
          }))] : [at(arrayPath, remove(~~targetKey)), at(arrayPath, insert(elementValue, position, referenceItemKey > targetKey ? ~~referenceItemKey - 1 : ~~referenceItemKey))];
        });
      }
    };
    return window.addEventListener("sanity/dragEnd", handler), () => {
      window.removeEventListener("sanity/dragEnd", handler);
    };
  }, t1 = [getDocument], $[0] = getDocument, $[1] = t0, $[2] = t1) : (t0 = $[1], t1 = $[2]), useEffect(t0, t1);
  const dispatchDragEndEvent = _temp$8;
  let t2;
  return $[3] === Symbol.for("react.memo_cache_sentinel") ? (t2 = {
    dispatchDragEndEvent
  }, $[3] = t2) : t2 = $[3], t2;
}
function _temp$8(event) {
  const customEvent = new CustomEvent("sanity/dragEnd", {
    detail: event,
    cancelable: !0
  });
  window.dispatchEvent(customEvent);
}
const SchemaContext = createContext(null);
function useSchema() {
  const context = useContext(SchemaContext);
  if (!context)
    throw new Error("Schema context is missing");
  return context;
}
function getArrayRemoveAction(node, doc) {
  if (!node.type) throw new Error("Node type is missing");
  return () => doc.patch(async ({
    getSnapshot
  }) => getArrayRemovePatches(node, await getSnapshot()));
}
function getArrayInsertAction(node, doc, insertType, position) {
  if (!node.type) throw new Error("Node type is missing");
  return () => doc.patch(() => getArrayInsertPatches(node, insertType, position));
}
function getDuplicateAction(node, doc) {
  if (!node.type) throw new Error("Node type is missing");
  return () => doc.patch(async ({
    getSnapshot
  }) => getArrayDuplicatePatches(node, await getSnapshot()));
}
function getContextMenuItems(context) {
  const {
    node,
    field,
    parent,
    doc
  } = context;
  return field?.type === "arrayItem" ? getContextMenuArrayItems({
    node,
    field,
    doc
  }) : parent?.type === "union" ? getContextMenuUnionItems({
    node,
    parent,
    doc
  }) : Promise.resolve([]);
}
function getDuplicateItem(context) {
  const {
    node,
    doc
  } = context;
  return doc ? [{
    type: "action",
    label: "Duplicate",
    icon: CopyIcon,
    action: getDuplicateAction(node, doc),
    telemetryEvent: "Visual Editing Context Menu Item Duplicated"
  }] : [];
}
function getRemoveItems(context) {
  const {
    node,
    doc
  } = context;
  return doc ? [{
    type: "action",
    label: "Remove",
    icon: RemoveIcon,
    action: getArrayRemoveAction(node, doc),
    telemetryEvent: "Visual Editing Context Menu Item Removed"
  }] : [];
}
async function getMoveItems(context, withDivider = !0) {
  const {
    node,
    doc
  } = context;
  if (!doc) return [];
  const items = [], groupItems = [], [moveUpPatches, moveDownPatches, moveFirstPatches, moveLastPatches] = await Promise.all([getArrayMovePatches(node, doc, "previous"), getArrayMovePatches(node, doc, "next"), getArrayMovePatches(node, doc, "first"), getArrayMovePatches(node, doc, "last")]);
  return moveFirstPatches.length && groupItems.push({
    type: "action",
    label: "To top",
    icon: PublishIcon,
    action: () => doc.patch(moveFirstPatches),
    telemetryEvent: "Visual Editing Context Menu Item Moved"
  }), moveUpPatches.length && groupItems.push({
    type: "action",
    label: "Up",
    icon: ArrowUpIcon,
    action: () => doc.patch(moveUpPatches),
    telemetryEvent: "Visual Editing Context Menu Item Moved"
  }), moveDownPatches.length && groupItems.push({
    type: "action",
    label: "Down",
    icon: ArrowDownIcon,
    action: () => doc.patch(moveDownPatches),
    telemetryEvent: "Visual Editing Context Menu Item Moved"
  }), moveLastPatches.length && groupItems.push({
    type: "action",
    label: "To bottom",
    icon: UnpublishIcon,
    action: () => doc.patch(moveLastPatches),
    telemetryEvent: "Visual Editing Context Menu Item Moved"
  }), groupItems.length && (items.push({
    type: "group",
    label: "Move",
    icon: SortIcon,
    items: groupItems
  }), withDivider && items.push({
    type: "divider"
  })), items;
}
async function getContextMenuArrayItems(context) {
  const {
    node,
    field,
    doc
  } = context, items = [];
  return items.push(...getDuplicateItem(context)), items.push(...getRemoveItems(context)), items.push(...await getMoveItems(context)), items.push({
    type: "action",
    label: "Insert before",
    icon: InsertAboveIcon,
    action: getArrayInsertAction(node, doc, field.name, "before"),
    telemetryEvent: "Visual Editing Context Menu Item Inserted"
  }), items.push({
    type: "action",
    label: "Insert after",
    icon: InsertBelowIcon,
    action: getArrayInsertAction(node, doc, field.name, "after"),
    telemetryEvent: "Visual Editing Context Menu Item Inserted"
  }), items;
}
const InsertMenuWrapper = (props) => {
  const $ = c(12), {
    label,
    parent,
    width,
    onSelect,
    boundaryElement
  } = props;
  let t0;
  $[0] === Symbol.for("react.memo_cache_sentinel") ? (t0 = ["left-start", "right", "left", "right-end", "left-end", "bottom", "top"], $[0] = t0) : t0 = $[0];
  let t1;
  $[1] === Symbol.for("react.memo_cache_sentinel") ? (t1 = [4, 4, 4, 4], $[1] = t1) : t1 = $[1];
  let t2;
  $[2] !== boundaryElement || $[3] !== width ? (t2 = {
    arrow: !1,
    constrainSize: !0,
    floatingBoundary: boundaryElement,
    padding: 0,
    placement: "right-start",
    fallbackPlacements: t0,
    preventOverflow: !0,
    width,
    __unstable_margins: t1
  }, $[2] = boundaryElement, $[3] = width, $[4] = t2) : t2 = $[4];
  let t3;
  $[5] !== onSelect || $[6] !== parent ? (t3 = /* @__PURE__ */ jsx(InsertMenu, { node: parent, onSelect }), $[5] = onSelect, $[6] = parent, $[7] = t3) : t3 = $[7];
  let t4;
  return $[8] !== label || $[9] !== t2 || $[10] !== t3 ? (t4 = /* @__PURE__ */ jsx(MenuGroup, { fontSize: 1, icon: InsertBelowIcon, padding: 2, popover: t2, space: 2, text: label, children: t3 }), $[8] = label, $[9] = t2, $[10] = t3, $[11] = t4) : t4 = $[11], t4;
};
async function getContextMenuUnionItems(context) {
  const {
    doc,
    node,
    parent
  } = context, items = [];
  if (items.push(...getDuplicateItem(context)), items.push(...getRemoveItems(context)), items.push(...await getMoveItems(context)), parent.options?.insertMenu) {
    const width = (parent.options.insertMenu || {}).views?.some((view) => view.name === "grid") ? 0 : void 0;
    items.push({
      type: "custom",
      component: ({
        boundaryElement,
        sendTelemetry
      }) => /* @__PURE__ */ jsx(InsertMenuWrapper, { label: "Insert before", onSelect: (schemaType) => {
        getArrayInsertAction(node, doc, schemaType.name, "before")(), sendTelemetry("Visual Editing Context Menu Item Inserted", null);
      }, parent, width, boundaryElement })
    }), items.push({
      type: "custom",
      component: ({
        boundaryElement,
        sendTelemetry
      }) => /* @__PURE__ */ jsx(InsertMenuWrapper, { label: "Insert after", onSelect: (schemaType) => {
        getArrayInsertAction(node, doc, schemaType.name, "after")(), sendTelemetry("Visual Editing Context Menu Item Inserted", null);
      }, parent, width, boundaryElement })
    });
  } else
    items.push({
      type: "group",
      label: "Insert before",
      icon: InsertAboveIcon,
      items: parent.of.filter((item) => item.type === "unionOption").map((t) => ({
        type: "action",
        icon: getNodeIcon(t),
        label: t.name === "block" ? "Paragraph" : t.title || t.name,
        action: getArrayInsertAction(node, doc, t.name, "before"),
        telemetryEvent: "Visual Editing Context Menu Item Inserted"
      }))
    }), items.push({
      type: "group",
      label: "Insert after",
      icon: InsertBelowIcon,
      items: parent.of.filter((item) => item.type === "unionOption").map((t) => ({
        type: "action",
        label: t.name === "block" ? "Paragraph" : t.title || t.name,
        icon: getNodeIcon(t),
        action: getArrayInsertAction(node, doc, t.name, "after"),
        telemetryEvent: "Visual Editing Context Menu Item Inserted"
      }))
    });
  return items;
}
const POPOVER_MARGINS = [-4, 4, -4, 4];
function ContextMenuItem(props) {
  const $ = c(27), {
    node,
    onDismiss,
    boundaryElement
  } = props, sendTelemetry = useTelemetry();
  let t0;
  $[0] !== node || $[1] !== onDismiss || $[2] !== sendTelemetry ? (t0 = () => {
    node.type === "action" && (node.action?.(), onDismiss?.(), node.telemetryEvent && sendTelemetry(node.telemetryEvent, null));
  }, $[0] = node, $[1] = onDismiss, $[2] = sendTelemetry, $[3] = t0) : t0 = $[3];
  const onClick = t0;
  if (node.type === "divider") {
    let t1;
    return $[4] === Symbol.for("react.memo_cache_sentinel") ? (t1 = /* @__PURE__ */ jsx(MenuDivider, {}), $[4] = t1) : t1 = $[4], t1;
  }
  if (node.type === "action") {
    const t1 = !node.action;
    let t2;
    return $[5] !== node.hotkeys || $[6] !== node.icon || $[7] !== node.label || $[8] !== onClick || $[9] !== t1 ? (t2 = /* @__PURE__ */ jsx(MenuItem, { fontSize: 1, hotkeys: node.hotkeys, icon: node.icon, padding: 2, space: 2, text: node.label, disabled: t1, onClick }), $[5] = node.hotkeys, $[6] = node.icon, $[7] = node.label, $[8] = onClick, $[9] = t1, $[10] = t2) : t2 = $[10], t2;
  }
  if (node.type === "group") {
    const t1 = node.icon;
    let t2;
    $[11] === Symbol.for("react.memo_cache_sentinel") ? (t2 = {
      arrow: !1,
      constrainSize: !0,
      placement: "right-start",
      fallbackPlacements: ["left-start", "right", "left", "right-end", "left-end", "bottom", "top"],
      preventOverflow: !0,
      __unstable_margins: POPOVER_MARGINS
    }, $[11] = t2) : t2 = $[11];
    const t3 = node.label;
    let t4;
    if ($[12] !== boundaryElement || $[13] !== node.items || $[14] !== onDismiss) {
      let t52;
      $[16] !== boundaryElement || $[17] !== onDismiss ? (t52 = (item, itemIndex) => /* @__PURE__ */ jsx(ContextMenuItem, { node: item, onDismiss, boundaryElement }, itemIndex), $[16] = boundaryElement, $[17] = onDismiss, $[18] = t52) : t52 = $[18], t4 = node.items.map(t52), $[12] = boundaryElement, $[13] = node.items, $[14] = onDismiss, $[15] = t4;
    } else
      t4 = $[15];
    let t5;
    return $[19] !== node.icon || $[20] !== node.label || $[21] !== t4 ? (t5 = /* @__PURE__ */ jsx(MenuGroup, { fontSize: 1, icon: t1, padding: 2, popover: t2, space: 2, text: t3, children: t4 }), $[19] = node.icon, $[20] = node.label, $[21] = t4, $[22] = t5) : t5 = $[22], t5;
  }
  if (node.type === "custom") {
    const {
      component: Component
    } = node;
    let t1;
    return $[23] !== Component || $[24] !== boundaryElement || $[25] !== sendTelemetry ? (t1 = /* @__PURE__ */ jsx(Component, { boundaryElement, sendTelemetry }), $[23] = Component, $[24] = boundaryElement, $[25] = sendTelemetry, $[26] = t1) : t1 = $[26], t1;
  }
  return null;
}
const ContextMenu = (props) => {
  const $ = c(43), {
    node,
    onDismiss,
    position: t0
  } = props, {
    x,
    y
  } = t0, [boundaryElement, setBoundaryElement] = useState(null), {
    getField
  } = useSchema(), {
    getDocument
  } = useDocuments();
  let t1;
  $[0] !== getField || $[1] !== node ? (t1 = getField(node), $[0] = getField, $[1] = node, $[2] = t1) : t1 = $[2];
  const {
    field,
    parent
  } = t1;
  let t2;
  t2 = field?.title || field?.name || "Unknown type";
  const title = t2, [items, setItems] = useState(void 0);
  let t3, t4;
  $[3] !== field || $[4] !== getDocument || $[5] !== node || $[6] !== parent ? (t3 = () => {
    (async () => {
      const doc = getDocument(node.id);
      if (!doc)
        return;
      const items_0 = await getContextMenuItems({
        node,
        field,
        parent,
        doc
      });
      setItems(items_0);
    })();
  }, t4 = [field, node, parent, getDocument], $[3] = field, $[4] = getDocument, $[5] = node, $[6] = parent, $[7] = t3, $[8] = t4) : (t3 = $[7], t4 = $[8]), useEffect(t3, t4);
  let t5, t6;
  $[9] !== x || $[10] !== y ? (t6 = {
    getBoundingClientRect: () => ({
      bottom: y,
      left: x,
      right: x,
      top: y,
      width: 0,
      height: 0
    })
  }, $[9] = x, $[10] = y, $[11] = t6) : t6 = $[11], t5 = t6;
  const contextMenuReferenceElement = t5;
  let t7, t8;
  $[12] !== field ? (t8 = getNodeIcon(field), $[12] = field, $[13] = t8) : t8 = $[13], t7 = t8;
  const icon = t7;
  let t9;
  $[14] === Symbol.for("react.memo_cache_sentinel") ? (t9 = {
    minWidth: 120,
    maxWidth: 160
  }, $[14] = t9) : t9 = $[14];
  let t10;
  $[15] !== icon || $[16] !== items ? (t10 = /* @__PURE__ */ jsx(Box, { flex: "none", children: items ? /* @__PURE__ */ jsx(Text, { size: 1, children: icon }) : /* @__PURE__ */ jsx(Spinner, { size: 1 }) }), $[15] = icon, $[16] = items, $[17] = t10) : t10 = $[17];
  const t11 = items ? title : "Loading...";
  let t12;
  $[18] !== t11 ? (t12 = /* @__PURE__ */ jsx(Stack, { flex: 1, space: 2, children: /* @__PURE__ */ jsx(Text, { size: 1, weight: "semibold", children: t11 }) }), $[18] = t11, $[19] = t12) : t12 = $[19];
  let t13;
  $[20] !== t10 || $[21] !== t12 ? (t13 = /* @__PURE__ */ jsxs(Flex, { gap: 2, padding: 2, children: [
    t10,
    t12
  ] }), $[20] = t10, $[21] = t12, $[22] = t13) : t13 = $[22];
  let t14;
  $[23] !== boundaryElement || $[24] !== items || $[25] !== onDismiss ? (t14 = items && /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(MenuDivider, {}),
    items.map((item, i) => /* @__PURE__ */ jsx(ContextMenuItem, { node: item, onDismiss, boundaryElement }, i))
  ] }), $[23] = boundaryElement, $[24] = items, $[25] = onDismiss, $[26] = t14) : t14 = $[26];
  let t15;
  $[27] !== t13 || $[28] !== t14 ? (t15 = /* @__PURE__ */ jsxs(Menu, { style: t9, children: [
    t13,
    t14
  ] }), $[27] = t13, $[28] = t14, $[29] = t15) : t15 = $[29];
  const t16 = `${x}-${y}`;
  let t17;
  $[30] !== x || $[31] !== y ? (t17 = {
    position: "absolute",
    left: x,
    top: y
  }, $[30] = x, $[31] = y, $[32] = t17) : t17 = $[32];
  let t18;
  $[33] !== t16 || $[34] !== t17 ? (t18 = /* @__PURE__ */ jsx("div", { style: t17 }, t16), $[33] = t16, $[34] = t17, $[35] = t18) : t18 = $[35];
  let t19;
  $[36] !== contextMenuReferenceElement || $[37] !== t15 || $[38] !== t18 ? (t19 = /* @__PURE__ */ jsx(Popover, { __unstable_margins: POPOVER_MARGINS, arrow: !1, open: !0, placement: "right-start", referenceElement: contextMenuReferenceElement, content: t15, children: t18 }), $[36] = contextMenuReferenceElement, $[37] = t15, $[38] = t18, $[39] = t19) : t19 = $[39];
  let t20;
  return $[40] !== onDismiss || $[41] !== t19 ? (t20 = /* @__PURE__ */ jsx(PopoverPortal, { setBoundaryElement, onDismiss, children: t19 }), $[40] = onDismiss, $[41] = t19, $[42] = t20) : t20 = $[42], t20;
};
function getLinkHref(href, referer) {
  try {
    const parsed = new URL(href, typeof location > "u" ? void 0 : location.origin);
    if (parsed.hash) {
      const hash = new URL(getLinkHref(parsed.hash.slice(1), referer));
      return `${parsed.origin}${parsed.pathname}${parsed.search}#${hash.pathname}${hash.search}`;
    }
    return parsed.searchParams.set("preview", referer), parsed.toString();
  } catch {
    return href;
  }
}
const PreviewSnapshotsContext = createContext(null);
function usePreviewSnapshots() {
  const context = useContext(PreviewSnapshotsContext);
  if (!context)
    throw new Error("Preview Snapshots context is missing");
  return context;
}
const isReactElementOverlayComponent = (component) => isValidElement(component), Root$3 = styled(Card)`
  background-color: var(--overlay-bg);
  border-radius: 3px;
  pointer-events: none;
  position: absolute;
  will-change: transform;
  box-shadow: var(--overlay-box-shadow);
  transition: none;

  --overlay-bg: transparent;
  --overlay-box-shadow: inset 0 0 0 1px transparent;

  [data-overlays] & {
    --overlay-bg: color-mix(in srgb, transparent 95%, var(--card-focus-ring-color));
    --overlay-box-shadow: inset 0 0 0 2px
      color-mix(in srgb, transparent 50%, var(--card-focus-ring-color));
  }

  [data-fading-out] & {
    transition:
      box-shadow 1550ms,
      background-color 1550ms;

    --overlay-bg: rgba(0, 0, 255, 0);
    --overlay-box-shadow: inset 0 0 0 1px transparent;
  }

  &[data-focused] {
    --overlay-box-shadow: inset 0 0 0 1px var(--card-focus-ring-color);
  }

  &[data-hovered]:not([data-focused]) {
    transition: none;
    --overlay-box-shadow: inset 0 0 0 2px var(--card-focus-ring-color);
  }

  /* [data-unmounted] & {
    --overlay-box-shadow: inset 0 0 0 1px var(--card-focus-ring-color);
  } */

  :link {
    text-decoration: none;
  }
`, Actions = styled(Flex)`
  bottom: 100%;
  cursor: pointer;
  pointer-events: none;
  position: absolute;
  right: 0;

  [data-hovered] & {
    pointer-events: all;
  }

  [data-flipped] & {
    bottom: auto;
    top: 100%;
  }
`, Tab = styled(Flex)`
  bottom: 100%;
  cursor: pointer;
  pointer-events: none;
  position: absolute;
  left: 0;

  [data-hovered] & {
    pointer-events: all;
  }

  [data-flipped] & {
    bottom: auto;
    top: 100%;
  }
`, ActionOpen = styled(Card)`
  cursor: pointer;
  background-color: var(--card-focus-ring-color);
  right: 0;
  border-radius: 3px;

  & [data-ui='Text'] {
    color: #fff;
    white-space: nowrap;
  }
`, Labels = styled(Flex)`
  display: flex;
  align-items: center;
  background-color: var(--card-focus-ring-color);
  right: 0;
  border-radius: 3px;
  & [data-ui='Text'],
  & [data-sanity-icon] {
    color: #fff;
    white-space: nowrap;
  }
`;
function createIntentLink(node) {
  const {
    id,
    type,
    path,
    baseUrl,
    tool,
    workspace
  } = node;
  return createEditUrl({
    baseUrl,
    workspace,
    tool,
    type,
    id,
    path: pathToUrlString(studioPath.fromString(path))
  });
}
const ElementOverlayInner = (props) => {
  const $ = c(35), {
    element,
    focused,
    componentResolver,
    node,
    showActions,
    draggable
  } = props, {
    getField,
    getType
  } = useSchema();
  let t0;
  $[0] !== getType || $[1] !== node ? (t0 = getType(node), $[0] = getType, $[1] = node, $[2] = t0) : t0 = $[2];
  const schemaType = t0;
  let t1;
  $[3] !== getField || $[4] !== node ? (t1 = getField(node), $[3] = getField, $[4] = node, $[5] = t1) : t1 = $[5];
  const {
    field,
    parent
  } = t1;
  let t2;
  $[6] !== node ? (t2 = "path" in node ? createIntentLink(node) : node.href, $[6] = node, $[7] = t2) : t2 = $[7];
  const href = t2, previewSnapshots = usePreviewSnapshots();
  let t3;
  bb0: {
    if (!("path" in node)) {
      t3 = void 0;
      break bb0;
    }
    let t42;
    $[8] !== node.id || $[9] !== previewSnapshots ? (t42 = previewSnapshots.find((snapshot) => snapshot._id === node.id)?.title, $[8] = node.id, $[9] = previewSnapshots, $[10] = t42) : t42 = $[10], t3 = t42;
  }
  const title = t3;
  let t4;
  bb1: {
    if (!("path" in node)) {
      t4 = void 0;
      break bb1;
    }
    if (!field || !schemaType) {
      t4 = void 0;
      break bb1;
    }
    const type = field.value.type, t52 = !!focused;
    let t62;
    $[11] !== element || $[12] !== field || $[13] !== node || $[14] !== parent || $[15] !== schemaType || $[16] !== t52 || $[17] !== type ? (t62 = {
      document: schemaType,
      element,
      field,
      focused: t52,
      node,
      parent,
      type
    }, $[11] = element, $[12] = field, $[13] = node, $[14] = parent, $[15] = schemaType, $[16] = t52, $[17] = type, $[18] = t62) : t62 = $[18], t4 = t62;
  }
  const componentContext = t4, customComponents = useCustomComponents(componentContext, componentResolver);
  let t5;
  $[19] !== schemaType ? (t5 = schemaType?.icon ? /* @__PURE__ */ jsx("div", { dangerouslySetInnerHTML: {
    __html: schemaType.icon
  } }) : /* @__PURE__ */ jsx(DocumentIcon, {}), $[19] = schemaType, $[20] = t5) : t5 = $[20];
  const icon = t5;
  let t6;
  $[21] !== href || $[22] !== showActions ? (t6 = showActions ? /* @__PURE__ */ jsx(Actions, { gap: 1, paddingY: 1, "data-sanity-overlay-element": !0, children: /* @__PURE__ */ jsx(Link, { href }) }) : null, $[21] = href, $[22] = showActions, $[23] = t6) : t6 = $[23];
  let t7;
  $[24] !== draggable || $[25] !== icon || $[26] !== title ? (t7 = title && /* @__PURE__ */ jsx(Tab, { gap: 1, paddingY: 1, children: /* @__PURE__ */ jsxs(Labels, { gap: 2, padding: 2, children: [
    draggable && /* @__PURE__ */ jsx(Box, { marginRight: 1, children: /* @__PURE__ */ jsx(Text, { className: "drag-handle", size: 0, children: /* @__PURE__ */ jsx(DragHandleIcon, {}) }) }),
    /* @__PURE__ */ jsx(Text, { size: 0, children: icon }),
    /* @__PURE__ */ jsx(Text, { size: 1, weight: "medium", children: title })
  ] }) }), $[24] = draggable, $[25] = icon, $[26] = title, $[27] = t7) : t7 = $[27];
  let t8;
  $[28] !== componentContext || $[29] !== customComponents ? (t8 = Array.isArray(customComponents) ? customComponents.map((t92, i) => {
    const {
      component: Component,
      props: props_0
    } = t92;
    return /* @__PURE__ */ jsx(Component, { PointerEvents, ...componentContext, ...props_0 }, i);
  }) : customComponents, $[28] = componentContext, $[29] = customComponents, $[30] = t8) : t8 = $[30];
  let t9;
  return $[31] !== t6 || $[32] !== t7 || $[33] !== t8 ? (t9 = /* @__PURE__ */ jsxs(Fragment, { children: [
    t6,
    t7,
    t8
  ] }), $[31] = t6, $[32] = t7, $[33] = t8, $[34] = t9) : t9 = $[34], t9;
}, ElementOverlay = memo(function(props) {
  const $ = c(24), {
    draggable,
    focused,
    hovered,
    rect,
    wasMaybeCollapsed,
    enableScrollIntoView
  } = props, ref = useRef(null), scrolledIntoViewRef = useRef(!1);
  let t0;
  const t1 = `${rect.w}px`, t2 = `${rect.h}px`, t3 = `translate(${rect.x}px, ${rect.y}px)`;
  let t4;
  $[0] !== t1 || $[1] !== t2 || $[2] !== t3 ? (t4 = {
    width: t1,
    height: t2,
    transform: t3
  }, $[0] = t1, $[1] = t2, $[2] = t3, $[3] = t4) : t4 = $[3], t0 = t4;
  const style = t0;
  let t5, t6;
  $[4] !== enableScrollIntoView || $[5] !== focused || $[6] !== wasMaybeCollapsed ? (t5 = () => {
    if (!scrolledIntoViewRef.current && !wasMaybeCollapsed && focused === !0 && ref.current && enableScrollIntoView) {
      const target = ref.current;
      scrollIntoView(ref.current, {
        behavior: (actions) => {
          actions.length !== 0 && target.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest"
          });
        },
        scrollMode: "if-needed",
        block: "center",
        inline: "nearest"
      });
    }
    scrolledIntoViewRef.current = focused === !0;
  }, t6 = [focused, wasMaybeCollapsed, enableScrollIntoView], $[4] = enableScrollIntoView, $[5] = focused, $[6] = wasMaybeCollapsed, $[7] = t5, $[8] = t6) : (t5 = $[7], t6 = $[8]), useEffect(t5, t6);
  const [isNearTop, setIsNearTop] = useState(!1);
  let t7;
  $[9] !== hovered ? (t7 = () => {
    if (!ref.current || !hovered)
      return;
    const io = new IntersectionObserver((t82) => {
      const [intersection] = t82;
      setIsNearTop(intersection.boundingClientRect.top < 0);
    }, {
      threshold: 1
    });
    return io.observe(ref.current), () => io.disconnect();
  }, $[9] = hovered, $[10] = t7) : t7 = $[10];
  let t8;
  $[11] !== hovered || $[12] !== isNearTop ? (t8 = [hovered, isNearTop], $[11] = hovered, $[12] = isNearTop, $[13] = t8) : t8 = $[13], useEffect(t7, t8);
  const t9 = focused ? "" : void 0, t10 = hovered ? "" : void 0, t11 = isNearTop ? "" : void 0, t12 = draggable ? "" : void 0;
  let t13;
  $[14] !== hovered || $[15] !== props ? (t13 = hovered && /* @__PURE__ */ jsx(ElementOverlayInner, { ...props }), $[14] = hovered, $[15] = props, $[16] = t13) : t13 = $[16];
  let t14;
  return $[17] !== style || $[18] !== t10 || $[19] !== t11 || $[20] !== t12 || $[21] !== t13 || $[22] !== t9 ? (t14 = /* @__PURE__ */ jsx(Root$3, { "data-focused": t9, "data-hovered": t10, "data-flipped": t11, "data-draggable": t12, ref, style, children: t13 }), $[17] = style, $[18] = t10, $[19] = t11, $[20] = t12, $[21] = t13, $[22] = t9, $[23] = t14) : t14 = $[23], t14;
});
function useCustomComponents(componentContext, componentResolver) {
  const $ = c(4);
  let t0;
  bb0: {
    if (!componentContext) {
      t0 = void 0;
      break bb0;
    }
    let t1;
    if ($[0] !== componentContext || $[1] !== componentResolver) {
      const resolved = componentResolver?.(componentContext);
      if (!resolved) {
        t0 = void 0;
        break bb0;
      }
      if (isReactElementOverlayComponent(resolved)) {
        t0 = resolved;
        break bb0;
      }
      t1 = (Array.isArray(resolved) ? resolved : [resolved]).map(_temp$7), $[0] = componentContext, $[1] = componentResolver, $[2] = t1, $[3] = t0;
    } else
      t1 = $[2], t0 = $[3];
    t0 = t1;
  }
  return t0;
}
function _temp$7(component) {
  return typeof component == "object" && "component" in component ? component : {
    component,
    props: {}
  };
}
const Link = memo(function(props) {
  const $ = c(6), referer = useSyncExternalStore(_temp2$5, _temp3$2);
  let t0, t1;
  $[0] !== props.href || $[1] !== referer ? (t1 = getLinkHref(props.href, referer), $[0] = props.href, $[1] = referer, $[2] = t1) : t1 = $[2], t0 = t1;
  const href = t0;
  let t2;
  $[3] === Symbol.for("react.memo_cache_sentinel") ? (t2 = /* @__PURE__ */ jsx(ActionOpen, { padding: 2, children: /* @__PURE__ */ jsx(Text, { size: 1, weight: "medium", children: "Open in Studio" }) }), $[3] = t2) : t2 = $[3];
  let t3;
  return $[4] !== href ? (t3 = /* @__PURE__ */ jsx(Box, { as: "a", href, target: "_blank", rel: "noopener noreferrer", children: t2 }), $[4] = href, $[5] = t3) : t3 = $[5], t3;
});
function _temp2$5(onStoreChange) {
  const handlePopState = () => onStoreChange();
  return window.addEventListener("popstate", handlePopState), () => window.removeEventListener("popstate", handlePopState);
}
function _temp3$2() {
  return window.location.href;
}
const OverlayDragGroupRect = (t0) => {
  const $ = c(5), {
    dragGroupRect
  } = t0, t1 = `${dragGroupRect.y}px`, t2 = `${dragGroupRect.x}px`, t3 = `${dragGroupRect.w - 1}px`, t4 = `${dragGroupRect.h - 1}px`;
  let t5;
  return $[0] !== t1 || $[1] !== t2 || $[2] !== t3 || $[3] !== t4 ? (t5 = /* @__PURE__ */ jsx("div", { style: {
    position: "absolute",
    top: t1,
    left: t2,
    width: t3,
    height: t4,
    border: "1px dashed #f0709b",
    pointerEvents: "none"
  } }), $[0] = t1, $[1] = t2, $[2] = t3, $[3] = t4, $[4] = t5) : t5 = $[4], t5;
};
function lerp(v0, v1, t) {
  return v0 * (1 - t) + v1 * t;
}
const OverlayDragInsertMarker = ({
  dragInsertPosition
}) => {
  if (dragInsertPosition === null) return;
  const flow = dragInsertPosition?.left || dragInsertPosition?.right ? "horizontal" : "vertical";
  let x = 0, y = 0, width = 0, height = 0;
  const offsetMultiplier = 0.0125;
  if (flow === "horizontal") {
    const {
      left,
      right
    } = dragInsertPosition;
    if (width = 6, right && left) {
      const startX = left.rect.x + left.rect.w, endX = right.rect.x, offset = Math.min(right.rect.h, left.rect.h) * offsetMultiplier;
      x = lerp(startX, endX, 0.5) - 6 / 2, y = left.rect.y + offset, height = Math.min(right.rect.h, left.rect.h) - offset * 2;
    } else if (right && !left) {
      const offset = right.rect.h * offsetMultiplier;
      x = right.rect.x - 6 / 2, y = right.rect.y + offset, height = right.rect.h - offset * 2;
    } else if (left && !right) {
      const offset = left.rect.h * offsetMultiplier;
      x = left.rect.x + left.rect.w - 6 / 2, y = left.rect.y + offset, height = left.rect.h - offset * 2;
    }
  } else {
    const {
      bottom,
      top
    } = dragInsertPosition;
    if (bottom && top) {
      const startX = Math.min(top.rect.x, bottom.rect.x), startY = top.rect.y + top.rect.h, endY = bottom.rect.y, offset = Math.min(bottom.rect.w, top.rect.w) * offsetMultiplier;
      height = 6, x = startX + offset, y = lerp(startY, endY, 0.5) - 6 / 2, width = Math.max(bottom.rect.w, top.rect.w) - offset * 2;
    } else if (bottom && !top) {
      const offset = bottom.rect.w * offsetMultiplier;
      x = bottom.rect.x + offset, y = bottom.rect.y - 6 / 2, width = bottom.rect.w - offset * 2, height = 6;
    } else if (top && !bottom) {
      const offset = top.rect.w * offsetMultiplier;
      x = top.rect.x + offset, y = top.rect.y + top.rect.h - 6 / 2, width = top.rect.w - offset * 2, height = 6;
    }
  }
  return /* @__PURE__ */ jsx("div", { style: {
    position: "absolute",
    width: `${width}px`,
    height: `${height}px`,
    transform: `translate(${x}px, ${y}px)`,
    background: "#556bfc",
    border: "2px solid white",
    borderRadius: "999px",
    zIndex: "999999"
  } });
}, Root$2 = styled.div`
  --drag-preview-opacity: 0.98;
  --drag-preview-skeleton-stroke: #ffffff;

  @media (prefers-color-scheme: dark) {
    --drag-preview-skeleton-stroke: #383d51;
  }

  position: fixed;
  display: grid;
  transform: ${({
  $scaleFactor,
  $width,
  $height
}) => `translate(calc(var(--drag-preview-x) - ${$width / 2}px), calc(var(--drag-preview-y) - ${$height / 2}px)) scale(${$scaleFactor})`};
  width: ${({
  $width
}) => `${$width}px`};
  height: ${({
  $height
}) => `${$height}px`};
  z-index: 9999999;
  opacity: var(--drag-preview-opacity);
  cursor: move;

  .drag-preview-content-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
    container-type: inline-size;
  }

  [data-ui='card'] {
    position: relative;
    width: 100%;
    height: 100%;
  }

  .drag-preview-skeleton {
    position: absolute;
    inset: 0;

    rect {
      stroke: var(--drag-preview-skeleton-stroke);
    }
  }

  .drag-preview-handle {
    position: absolute;
    top: 4cqmin;
    left: 4cqmin;
    width: 6cqmin;
    fill: var(--drag-preview-handle-fill);
  }
`;
function clamp(number, min, max) {
  return number < min ? min : number > max ? max : number;
}
function map(number, inMin, inMax, outMin, outMax) {
  const mapped = (number - inMin) * (outMax - outMin) / (inMax - inMin) + outMin;
  return clamp(mapped, outMin, outMax);
}
const OverlayDragPreview = (t0) => {
  const $ = c(24), {
    skeleton
  } = t0, maxSkeletonWidth = Math.min(skeleton.maxWidth, window.innerWidth / 2), scaleFactor = skeleton.w > maxSkeletonWidth ? maxSkeletonWidth / skeleton.w : 1, offsetX = skeleton.offsetX * scaleFactor, offsetY = skeleton.offsetY * scaleFactor, prefersDark = usePrefersDark(), theme = useTheme_v2(), radius = theme.radius[~~map(skeleton.w, 0, 1920, 1, theme.radius.length - 2)];
  let t1;
  $[0] !== skeleton.childRects ? (t1 = skeleton.childRects.filter(_temp$6), $[0] = skeleton.childRects, $[1] = t1) : t1 = $[1];
  const imageRects = t1;
  let t2;
  $[2] !== skeleton.childRects ? (t2 = skeleton.childRects.filter(_temp2$4), $[2] = skeleton.childRects, $[3] = t2) : t2 = $[3];
  const textRects = t2, t3 = skeleton.w, t4 = skeleton.h, t5 = prefersDark ? "dark" : "light", t6 = `0 0 ${skeleton.w} ${skeleton.h}`;
  let t7;
  $[4] !== imageRects || $[5] !== textRects ? (t7 = [...imageRects, ...textRects], $[4] = imageRects, $[5] = textRects, $[6] = t7) : t7 = $[6];
  let t8;
  $[7] !== t7 || $[8] !== theme.color.skeleton.from ? (t8 = t7.map((r_1, i) => /* @__PURE__ */ jsx("rect", { x: r_1.x, y: r_1.y, width: r_1.w, height: r_1.h, fill: theme.color.skeleton.from }, i)), $[7] = t7, $[8] = theme.color.skeleton.from, $[9] = t8) : t8 = $[9];
  let t9;
  $[10] !== t6 || $[11] !== t8 ? (t9 = /* @__PURE__ */ jsx("div", { className: "drag-preview-content-wrapper", children: /* @__PURE__ */ jsx("svg", { className: "drag-preview-skeleton", viewBox: t6, children: t8 }) }), $[10] = t6, $[11] = t8, $[12] = t9) : t9 = $[12];
  let t10;
  $[13] !== radius || $[14] !== t5 || $[15] !== t9 ? (t10 = /* @__PURE__ */ jsx(Card, { radius, shadow: 4, overflow: "hidden", tone: "transparent", scheme: t5, children: t9 }), $[13] = radius, $[14] = t5, $[15] = t9, $[16] = t10) : t10 = $[16];
  let t11;
  return $[17] !== offsetX || $[18] !== offsetY || $[19] !== scaleFactor || $[20] !== skeleton.h || $[21] !== skeleton.w || $[22] !== t10 ? (t11 = /* @__PURE__ */ jsx(Root$2, { $width: t3, $height: t4, $offsetX: offsetX, $offsetY: offsetY, $scaleFactor: scaleFactor, children: t10 }), $[17] = offsetX, $[18] = offsetY, $[19] = scaleFactor, $[20] = skeleton.h, $[21] = skeleton.w, $[22] = t10, $[23] = t11) : t11 = $[23], t11;
};
function _temp$6(r) {
  return r.tagName === "IMG";
}
function _temp2$4(r_0) {
  return r_0.tagName !== "IMG";
}
const Root$1 = styled(Card)`
  position: fixed;
  bottom: 2rem;
  left: 2rem;
`, OverlayMinimapPrompt = () => {
  const $ = c(2);
  let t0;
  $[0] === Symbol.for("react.memo_cache_sentinel") ? (t0 = {
    zIndex: "999999"
  }, $[0] = t0) : t0 = $[0];
  let t1;
  return $[1] === Symbol.for("react.memo_cache_sentinel") ? (t1 = /* @__PURE__ */ jsx(Root$1, { padding: 2, shadow: 2, radius: 2, style: t0, children: /* @__PURE__ */ jsxs(Flex, { align: "center", gap: 2, children: [
    /* @__PURE__ */ jsx(Hotkeys, { keys: ["Shift"] }),
    /* @__PURE__ */ jsx(Text, { size: 1, children: "Zoom Out" }),
    /* @__PURE__ */ jsx(ExpandIcon, {})
  ] }) }), $[1] = t1) : t1 = $[1], t1;
}, elementsReducer = (elements, message) => {
  const {
    type
  } = message;
  switch (type) {
    case "element/register":
      return elements.find((e) => e.id === message.id) ? elements : [...elements, {
        id: message.id,
        activated: !1,
        element: message.element,
        focused: !1,
        hovered: !1,
        rect: message.rect,
        sanity: message.sanity,
        dragDisabled: message.dragDisabled
      }];
    case "element/activate":
      return elements.map((e) => e.id === message.id ? {
        ...e,
        activated: !0
      } : e);
    case "element/update":
      return elements.map((e) => e.id === message.id ? {
        ...e,
        sanity: message.sanity,
        rect: message.rect
      } : e);
    case "element/unregister":
      return elements.filter((e) => e.id !== message.id);
    case "element/deactivate":
      return elements.map((e) => e.id === message.id ? {
        ...e,
        activated: !1,
        hovered: !1
      } : e);
    case "element/mouseenter":
      return elements.map((e) => e.id === message.id ? {
        ...e,
        rect: message.rect,
        hovered: !0
      } : {
        ...e,
        hovered: !1
      });
    case "element/mouseleave":
      return elements.map((element) => element.id === message.id ? {
        ...element,
        hovered: !1
      } : element);
    case "element/updateRect":
      return elements.map((element) => element.id === message.id ? {
        ...element,
        rect: message.rect
      } : element);
    case "element/click":
      return elements.map((e) => ({
        ...e,
        focused: e.id === message.id && "clicked"
      }));
    case "overlay/blur":
      return elements.map((e) => ({
        ...e,
        focused: !1
      }));
    case "presentation/blur":
      return elements.map((e) => ({
        ...e,
        focused: !1
      }));
    case "presentation/focus": {
      const clickedElement = elements.find((e) => e.focused === "clicked");
      return elements.map((e) => {
        const focused = "path" in e.sanity && e.sanity.id === message.data.id && e.sanity.path === message.data.path;
        return clickedElement && e === clickedElement && focused ? e : {
          ...e,
          // Mark as a dupe if another matching item has been clicked to prevent
          // scrolling, otherwise just set focus as a boolean
          focused: focused && clickedElement ? "duplicate" : focused
        };
      });
    }
    default:
      return elements;
  }
};
function overlayStateReducer(state, message) {
  const {
    type
  } = message;
  let {
    contextMenu,
    focusPath,
    perspective,
    isDragging,
    dragInsertPosition,
    dragShowMinimap,
    dragShowMinimapPrompt,
    dragSkeleton,
    dragMinimapTransition,
    dragGroupRect
  } = state, wasMaybeCollapsed = !1;
  if (type === "presentation/focus") {
    const prevFocusPath = state.focusPath;
    focusPath = message.data.path, prevFocusPath !== focusPath && (wasMaybeCollapsed = prevFocusPath.slice(focusPath.length).startsWith("["));
  }
  return type === "presentation/perspective" && (perspective = message.data.perspective), type === "element/contextmenu" && ("sanity" in message ? contextMenu = {
    node: message.sanity,
    position: message.position
  } : contextMenu = null), (type === "element/click" || type === "element/mouseleave" || type === "overlay/blur" || type === "presentation/blur" || type === "presentation/focus") && (contextMenu = null), type === "overlay/dragUpdateInsertPosition" && (dragInsertPosition = message.insertPosition), type === "overlay/dragStart" && (isDragging = !0), message.type === "overlay/dragUpdateSkeleton" && (dragSkeleton = message.skeleton), type === "overlay/dragEnd" && (isDragging = !1), message.type === "overlay/dragToggleMinimapPrompt" && (dragShowMinimapPrompt = message.display), type === "overlay/dragStartMinimapTransition" && (dragMinimapTransition = !0), type === "overlay/dragEndMinimapTransition" && (dragMinimapTransition = !1), type === "overlay/dragUpdateGroupRect" && (dragGroupRect = message.groupRect), type === "overlay/dragToggleMinimap" && (dragShowMinimap = message.display), {
    ...state,
    contextMenu,
    elements: elementsReducer(state.elements, message),
    dragInsertPosition,
    dragSkeleton,
    dragGroupRect,
    isDragging,
    focusPath,
    perspective,
    wasMaybeCollapsed,
    dragShowMinimap,
    dragShowMinimapPrompt,
    dragMinimapTransition
  };
}
const PreviewSnapshotsProvider = function(props) {
  const $ = c(13), {
    comlink,
    children
  } = props;
  let t0;
  $[0] === Symbol.for("react.memo_cache_sentinel") ? (t0 = [], $[0] = t0) : t0 = $[0];
  const [previewSnapshots, setPreviewSnapshots] = useState(t0);
  let t1;
  $[1] !== comlink ? (t1 = async (signal) => {
    if (comlink)
      try {
        const response = await comlink.fetch("visual-editing/preview-snapshots", void 0, {
          signal,
          suppressWarnings: !0
        });
        setPreviewSnapshots(response.snapshots);
      } catch {
      }
  }, $[1] = comlink, $[2] = t1) : t1 = $[2];
  const fetchPreviewSnapshots = t1;
  let t2, t3;
  $[3] !== comlink || $[4] !== fetchPreviewSnapshots ? (t2 = () => {
    if (!comlink)
      return;
    const previewSapshotsFetch = new AbortController(), unsub = comlink.onStatus(() => {
      fetchPreviewSnapshots(previewSapshotsFetch.signal);
    }, "connected");
    return () => {
      previewSapshotsFetch.abort(), unsub();
    };
  }, t3 = [comlink, fetchPreviewSnapshots], $[3] = comlink, $[4] = fetchPreviewSnapshots, $[5] = t2, $[6] = t3) : (t2 = $[5], t3 = $[6]), useEffect(t2, t3);
  let t4, t5;
  $[7] !== comlink ? (t4 = () => comlink?.on("presentation/preview-snapshots", (data) => {
    setPreviewSnapshots(data.snapshots);
  }), t5 = [comlink], $[7] = comlink, $[8] = t4, $[9] = t5) : (t4 = $[8], t5 = $[9]), useEffect(t4, t5);
  let t6;
  t6 = previewSnapshots;
  const context = t6;
  let t7;
  return $[10] !== children || $[11] !== context ? (t7 = /* @__PURE__ */ jsx(PreviewSnapshotsContext.Provider, { value: context, children }), $[10] = children, $[11] = context, $[12] = t7) : t7 = $[12], t7;
};
function isSanityNode(node) {
  return "path" in node;
}
function isDocumentSchemaType(type) {
  return type.type === "document";
}
function isTypeSchemaType(type) {
  return type.type === "type";
}
function popUnkeyedPathSegments(path) {
  return path.split(".").toReversed().reduce((acc, part) => acc.length ? [part, ...acc] : part.includes("[_key==") ? [part] : [], []).join(".");
}
function getPathsWithUnresolvedTypes(elements) {
  return elements.reduce((acc, element) => {
    const {
      sanity
    } = element;
    if (!("id" in sanity) || !sanity.path.includes("[_key==")) return acc;
    const path = popUnkeyedPathSegments(sanity.path);
    return acc.find((item) => item.id === sanity.id && item.path === path) || acc.push({
      id: sanity.id,
      path
    }), acc;
  }, []);
}
const SchemaProvider = function(props) {
  const $ = c(27), {
    comlink,
    children,
    elements
  } = props;
  let t0;
  $[0] === Symbol.for("react.memo_cache_sentinel") ? (t0 = /* @__PURE__ */ new Map(), $[0] = t0) : t0 = $[0];
  const [resolvedTypes, setResolvedTypes] = useState(t0), [schema, setSchema] = useState(null);
  let t1;
  $[1] !== comlink ? (t1 = async (signal) => {
    if (comlink)
      try {
        const response = await comlink.fetch("visual-editing/schema", void 0, {
          signal,
          suppressWarnings: !0
        });
        setSchema(response.schema);
      } catch {
      }
  }, $[1] = comlink, $[2] = t1) : t1 = $[2];
  const fetchSchema = t1;
  let t2, t3;
  $[3] !== comlink || $[4] !== fetchSchema ? (t2 = () => {
    if (!comlink)
      return;
    const schemaFetch = new AbortController(), unsub = comlink.onStatus(() => {
      fetchSchema(schemaFetch.signal);
    }, "connected");
    return () => {
      schemaFetch.abort(), unsub();
    };
  }, t3 = [comlink, fetchSchema], $[3] = comlink, $[4] = fetchSchema, $[5] = t2, $[6] = t3) : (t2 = $[5], t3 = $[6]), useEffect(t2, t3);
  let t4;
  $[7] === Symbol.for("react.memo_cache_sentinel") ? (t4 = [], $[7] = t4) : t4 = $[7];
  const reportedPathsRef = useRef(t4);
  let t5, t6;
  $[8] !== comlink || $[9] !== elements ? (t5 = () => {
    const reportPaths = async (paths, signal_0) => {
      if (!(!paths.length || !comlink))
        try {
          const response_0 = await comlink.fetch("visual-editing/schema-union-types", {
            paths
          }, {
            signal: signal_0,
            suppressWarnings: !0
          });
          setResolvedTypes(response_0.types), reportedPathsRef.current = paths;
        } catch {
        }
    }, controller = new AbortController(), paths_0 = getPathsWithUnresolvedTypes(elements);
    return paths_0.some((p) => !reportedPathsRef.current.find((t82) => {
      const {
        id,
        path
      } = t82;
      return id === p.id && path === p.path;
    })) && reportPaths(paths_0, controller.signal), () => controller.abort();
  }, t6 = [comlink, elements], $[8] = comlink, $[9] = elements, $[10] = t5, $[11] = t6) : (t5 = $[10], t6 = $[11]), useEffect(t5, t6);
  let t7;
  $[12] !== schema ? (t7 = (node, _type) => {
    const type = _type || "document";
    if (!schema || typeof node != "string" && (!isSanityNode(node) || !Array.isArray(schema)))
      return;
    const name = typeof node == "string" ? node : node.type, filter = type === "document" ? isDocumentSchemaType : isTypeSchemaType;
    return schema.filter(filter).find((schemaType) => schemaType.name === name);
  }, $[12] = schema, $[13] = t7) : t7 = $[13];
  const getType = t7;
  let t8;
  $[14] !== getType || $[15] !== resolvedTypes ? (t8 = (node_0) => {
    if (!isSanityNode(node_0))
      return {
        field: void 0,
        parent: void 0
      };
    const schemaType_0 = getType(node_0);
    if (!schemaType_0)
      return {
        field: void 0,
        parent: void 0
      };
    function fieldFromPath(schemaType_1, path_0, parent, t92) {
      const prevPath = t92 === void 0 ? [] : t92;
      if (!schemaType_1)
        return {
          field: void 0,
          parent: void 0
        };
      const [next, ...rest] = path_0;
      if ("fields" in schemaType_1) {
        const objectField = schemaType_1.fields[next];
        if (!objectField && "rest" in schemaType_1)
          return fieldFromPath(schemaType_1.rest, path_0, schemaType_1, prevPath);
        if (!rest.length)
          return {
            field: objectField,
            parent
          };
        if (!objectField)
          throw new Error(`[@sanity/visual-editing] No field could be resolved at path: "${[...prevPath, ...path_0].join(".")}"`);
        return fieldFromPath(objectField.value, rest, schemaType_1, [...prevPath, next]);
      } else {
        if (schemaType_1.type === "array")
          return fieldFromPath(schemaType_1.of, path_0, schemaType_1, prevPath);
        if (schemaType_1.type === "arrayItem")
          return rest.length ? fieldFromPath(schemaType_1.value, rest, schemaType_1, [...prevPath, next]) : {
            field: schemaType_1,
            parent
          };
        if (schemaType_1.type === "union") {
          const name_0 = next.startsWith("[_key==") ? resolvedTypes?.get(node_0.id)?.get([prevPath.join("."), next].filter(Boolean).join("")) : next;
          return fieldFromPath(schemaType_1.of.find((item) => item.type === "unionOption" ? item.name === name_0 : item), rest, schemaType_1, [...prevPath, next]);
        } else {
          if (schemaType_1.type === "unionOption")
            return next ? fieldFromPath(schemaType_1.value, path_0, schemaType_1, prevPath) : {
              field: schemaType_1,
              parent
            };
          if (schemaType_1.type === "inline") {
            const type_0 = getType(schemaType_1.name, "type");
            return fieldFromPath(type_0.value, path_0, schemaType_1, prevPath);
          }
        }
      }
      throw new Error(`[@sanity/visual-editing] No field could be resolved at path: "${[...prevPath, ...path_0].join(".")}"`);
    }
    const nodePath = node_0.path.split(".").flatMap(_temp$5);
    try {
      return fieldFromPath(schemaType_0, nodePath, void 0);
    } catch (t102) {
      const e_1 = t102;
      return e_1 instanceof Error && console.warn(e_1.message), {
        field: void 0,
        parent: void 0
      };
    }
  }, $[14] = getType, $[15] = resolvedTypes, $[16] = t8) : t8 = $[16];
  const getField = t8;
  let t9, t10;
  $[17] !== schema ? (t10 = schema || [], $[17] = schema, $[18] = t10) : t10 = $[18];
  let t11;
  $[19] !== getField || $[20] !== getType || $[21] !== resolvedTypes || $[22] !== t10 ? (t11 = {
    getField,
    getType,
    resolvedTypes,
    schema: t10
  }, $[19] = getField, $[20] = getType, $[21] = resolvedTypes, $[22] = t10, $[23] = t11) : t11 = $[23], t9 = t11;
  const context = t9;
  let t12;
  return $[24] !== children || $[25] !== context ? (t12 = /* @__PURE__ */ jsx(SchemaContext.Provider, { value: context, children }), $[24] = children, $[25] = context, $[26] = t12) : t12 = $[26], t12;
};
function _temp$5(part) {
  return part.includes("[") ? part.split(/(\[.+\])/, 2) : [part];
}
const createStore = (initialState) => {
  let state = initialState;
  const getState = () => state, listeners = /* @__PURE__ */ new Set();
  return {
    getState,
    setState: (fn) => {
      state = fn(state), listeners.forEach((l) => l());
    },
    subscribe: (listener) => (listeners.add(listener), () => listeners.delete(listener))
  };
}, store = createStore({}), SharedStateProvider = (props) => {
  const $ = c(11), {
    comlink,
    children
  } = props;
  let t0, t1;
  $[0] !== comlink ? (t0 = () => comlink?.on("presentation/shared-state", _temp$4), t1 = [comlink], $[0] = comlink, $[1] = t0, $[2] = t1) : (t0 = $[1], t1 = $[2]), useEffect(t0, t1);
  let t2, t3;
  $[3] !== comlink ? (t2 = () => {
    (async function() {
      const value = await comlink?.fetch("visual-editing/shared-state", void 0, {
        suppressWarnings: !0
      });
      value && store.setState(() => value.state);
    })().catch(_temp2$3);
  }, t3 = [comlink], $[3] = comlink, $[4] = t2, $[5] = t3) : (t2 = $[4], t3 = $[5]), useEffect(t2, t3);
  let t4, t5;
  $[6] !== comlink ? (t5 = {
    comlink,
    store
  }, $[6] = comlink, $[7] = t5) : t5 = $[7], t4 = t5;
  const value_0 = t4;
  let t6;
  return $[8] !== children || $[9] !== value_0 ? (t6 = /* @__PURE__ */ jsx(SharedStateContext.Provider, { value: value_0, children }), $[8] = children, $[9] = value_0, $[10] = t6) : t6 = $[10], t6;
};
function _temp$4(data) {
  "value" in data ? store.setState((prev) => ({
    ...prev,
    [data.key]: data.value
  })) : store.setState((prev_0) => Object.fromEntries(Object.entries(prev_0).filter((t0) => {
    const [key] = t0;
    return key !== data.key;
  })));
}
function _temp2$3(reason) {
  console.debug(reason), console.warn("[@sanity/visual-editing]: Failed to fetch shared state. Check your version of `sanity` is up-to-date");
}
const TelemetryProvider = (t0) => {
  const $ = c(5), {
    children,
    comlink
  } = t0;
  let t1;
  $[0] !== comlink ? (t1 = (name, data) => {
    if (!comlink)
      return;
    const event = events[name];
    if (event)
      comlink.post("visual-editing/telemetry-log", {
        event,
        data
      });
    else
      throw new Error(`Telemetry event: ${name} does not exist`);
  }, $[0] = comlink, $[1] = t1) : t1 = $[1];
  const log = t1;
  let t2;
  return $[2] !== children || $[3] !== log ? (t2 = /* @__PURE__ */ jsx(TelemetryContext.Provider, { value: log, children }), $[2] = children, $[3] = log, $[4] = t2) : t2 = $[4], t2;
};
function useController(element, handler, inFrame, inPopUp) {
  const $ = c(7), overlayController = useRef(void 0), optimisticActorReady = useOptimisticActorReady();
  let t0, t1;
  return $[0] !== element || $[1] !== handler || $[2] !== inFrame || $[3] !== inPopUp || $[4] !== optimisticActorReady ? (t0 = () => {
    if (element)
      return overlayController.current = createOverlayController({
        handler,
        overlayElement: element,
        inFrame,
        inPopUp,
        optimisticActorReady
      }), () => {
        overlayController.current?.destroy(), overlayController.current = void 0;
      };
  }, t1 = [element, handler, inFrame, inPopUp, optimisticActorReady], $[0] = element, $[1] = handler, $[2] = inFrame, $[3] = inPopUp, $[4] = optimisticActorReady, $[5] = t0, $[6] = t1) : (t0 = $[5], t1 = $[6]), useEffect(t0, t1), overlayController;
}
function usePerspectiveSync(comlink, dispatch) {
  const $ = c(4);
  let t0, t1;
  $[0] !== comlink || $[1] !== dispatch ? (t0 = () => {
    const controller = new AbortController();
    comlink?.fetch("visual-editing/fetch-perspective", void 0, {
      signal: controller.signal,
      suppressWarnings: !0
    }).then((data) => {
      dispatch({
        type: "presentation/perspective",
        data
      });
    }).catch(_temp$3);
    const unsub = comlink?.on("presentation/perspective", (data_0) => {
      dispatch({
        type: "presentation/perspective",
        data: data_0
      });
    });
    return () => {
      unsub?.(), controller.abort();
    };
  }, t1 = [comlink, dispatch], $[0] = comlink, $[1] = dispatch, $[2] = t0, $[3] = t1) : (t0 = $[2], t1 = $[3]), useEffect(t0, t1);
}
function _temp$3() {
}
function isEqualSets(a, b) {
  if (a === b) return !0;
  if (a.size !== b.size) return !1;
  for (const value of a) if (!b.has(value)) return !1;
  return !0;
}
function useReportDocuments(comlink, elements, perspective) {
  const $ = c(7), lastReported = useRef(void 0);
  let t0;
  $[0] !== comlink ? (t0 = (documents, perspective_0) => {
    comlink?.post("visual-editing/documents", {
      documents,
      perspective: perspective_0
    });
  }, $[0] = comlink, $[1] = t0) : t0 = $[1];
  const reportDocuments = t0;
  let t1, t2;
  $[2] !== elements || $[3] !== perspective || $[4] !== reportDocuments ? (t1 = () => {
    const nodes = elements.map(_temp$2).filter(_temp2$2), nodeIds = new Set(nodes.map(_temp3$1));
    if (!lastReported.current || !isEqualSets(nodeIds, lastReported.current.nodeIds) || perspective !== lastReported.current.perspective) {
      const documentsOnPage = Array.from(nodeIds).map((_id) => {
        const node_0 = nodes.find((node) => node.id === _id), {
          type,
          projectId: _projectId,
          dataset: _dataset
        } = node_0;
        return _projectId && _dataset ? {
          _id,
          _type: type,
          _projectId,
          _dataset
        } : {
          _id,
          _type: type
        };
      });
      lastReported.current = {
        nodeIds,
        perspective
      }, reportDocuments(documentsOnPage, perspective);
    }
  }, t2 = [elements, perspective, reportDocuments], $[2] = elements, $[3] = perspective, $[4] = reportDocuments, $[5] = t1, $[6] = t2) : (t1 = $[5], t2 = $[6]), useEffect(t1, t2);
}
function _temp3$1(e_0) {
  return e_0.id;
}
function _temp2$2(s) {
  return !!s;
}
function _temp$2(e) {
  const {
    sanity
  } = e;
  return "id" in sanity ? sanity : null;
}
const Root = styled.div`
  background-color: transparent;
  direction: ltr;
  inset: 0;
  pointer-events: none;
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: ${({
  $zIndex
}) => $zIndex ?? "9999999"};
`;
function raf2(fn) {
  let r0, r1;
  return r0 = requestAnimationFrame(() => {
    r1 = requestAnimationFrame(fn);
  }), () => {
    r0 !== void 0 && cancelAnimationFrame(r0), r1 !== void 0 && cancelAnimationFrame(r1);
  };
}
const DocumentReporter = (props) => {
  const $ = c(8), {
    documentIds
  } = props;
  let t0;
  $[0] === Symbol.for("react.memo_cache_sentinel") ? (t0 = [], $[0] = t0) : t0 = $[0];
  const [uniqueIds, setUniqueIds] = useState(t0);
  let t1, t2;
  $[1] !== documentIds ? (t1 = () => {
    setUniqueIds((prev) => {
      const next = Array.from(new Set(documentIds));
      return prev.length === next.length && prev.reduce(_temp$1, next)?.length === 0 ? prev : next;
    });
  }, t2 = [documentIds], $[1] = documentIds, $[2] = t1, $[3] = t2) : (t1 = $[2], t2 = $[3]), useEffect(t1, t2);
  const actor = useOptimisticActor();
  let t3, t4;
  return $[4] !== actor || $[5] !== uniqueIds ? (t3 = () => {
    for (const id_0 of uniqueIds)
      actor.send({
        type: "observe",
        documentId: getDraftId(id_0)
      }), actor.send({
        type: "observe",
        documentId: getPublishedId(id_0)
      });
    return () => {
      for (const id_1 of uniqueIds)
        actor.send({
          type: "unobserve",
          documentId: getDraftId(id_1)
        }), actor.send({
          type: "unobserve",
          documentId: getPublishedId(id_1)
        });
    };
  }, t4 = [actor, uniqueIds], $[4] = actor, $[5] = uniqueIds, $[6] = t3, $[7] = t4) : (t3 = $[6], t4 = $[7]), useEffect(t3, t4), null;
}, OverlaysController = (props) => {
  const $ = c(12), {
    comlink,
    dispatch,
    inFrame,
    inPopUp,
    onDrag,
    overlayEnabled,
    rootElement
  } = props, {
    dispatchDragEndEvent
  } = useDragEndEvents(), sendTelemetry = useTelemetry();
  let t0;
  $[0] !== comlink || $[1] !== dispatch || $[2] !== dispatchDragEndEvent || $[3] !== onDrag || $[4] !== sendTelemetry ? (t0 = (message) => {
    if (message.type === "element/click") {
      const {
        sanity
      } = message;
      comlink?.post("visual-editing/focus", sanity), sendTelemetry("Visual Editing Overlay Clicked", null);
    } else if (message.type === "overlay/activate")
      comlink?.post("visual-editing/toggle", {
        enabled: !0
      });
    else if (message.type === "overlay/deactivate")
      comlink?.post("visual-editing/toggle", {
        enabled: !1
      });
    else if (message.type === "overlay/dragEnd") {
      const {
        insertPosition,
        target,
        dragGroup,
        flow,
        preventInsertDefault
      } = message;
      dispatchDragEndEvent({
        insertPosition,
        target,
        dragGroup,
        flow,
        preventInsertDefault
      }), insertPosition && sendTelemetry("Visual Editing Drag Sequence Completed", null);
    } else if (message.type === "overlay/dragUpdateCursorPosition") {
      onDrag(message.x, message.y);
      return;
    } else if (message.type === "overlay/dragToggleMinimap" && message.display === !0)
      sendTelemetry("Visual Editing Drag Minimap Enabled", null);
    else if (message.type === "overlay/setCursor") {
      const {
        element,
        cursor
      } = message;
      cursor ? element.style.cursor = cursor : element.style.removeProperty("cursor");
    }
    dispatch(message);
  }, $[0] = comlink, $[1] = dispatch, $[2] = dispatchDragEndEvent, $[3] = onDrag, $[4] = sendTelemetry, $[5] = t0) : t0 = $[5];
  const controller = useController(rootElement, t0, inFrame, inPopUp);
  let t1;
  $[6] !== controller.current || $[7] !== overlayEnabled ? (t1 = () => {
    overlayEnabled ? controller.current?.activate() : controller.current?.deactivate();
  }, $[6] = controller.current, $[7] = overlayEnabled, $[8] = t1) : t1 = $[8];
  let t2;
  return $[9] !== controller || $[10] !== overlayEnabled ? (t2 = [controller, overlayEnabled], $[9] = controller, $[10] = overlayEnabled, $[11] = t2) : t2 = $[11], useEffect(t1, t2), null;
}, Overlays = (props) => {
  const $ = c(74), {
    comlink,
    comlinkStatus,
    componentResolver: _componentResolver,
    inFrame,
    inPopUp,
    zIndex
  } = props, prefersDark = usePrefersDark();
  let t0;
  $[0] === Symbol.for("react.memo_cache_sentinel") ? (t0 = {
    contextMenu: null,
    dragInsertPosition: null,
    dragShowMinimap: !1,
    dragShowMinimapPrompt: !1,
    dragSkeleton: null,
    elements: [],
    focusPath: "",
    isDragging: !1,
    perspective: "published",
    wasMaybeCollapsed: !1,
    dragMinimapTransition: !1,
    dragGroupRect: null
  }, $[0] = t0) : t0 = $[0];
  const [t1, dispatch] = useReducer(overlayStateReducer, t0), {
    contextMenu,
    dragInsertPosition,
    dragShowMinimap,
    dragShowMinimapPrompt,
    dragSkeleton,
    elements,
    isDragging,
    perspective,
    wasMaybeCollapsed,
    dragMinimapTransition,
    dragGroupRect
  } = t1, [rootElement, setRootElement] = useState(null), [overlayEnabled, setOverlayEnabled] = useState(!0);
  let t2, t3;
  $[1] !== comlink ? (t2 = () => {
    const unsubs = [comlink?.on("presentation/focus", (data) => {
      dispatch({
        type: "presentation/focus",
        data
      });
    }), comlink?.on("presentation/blur", (data_0) => {
      dispatch({
        type: "presentation/blur",
        data: data_0
      });
    }), comlink?.on("presentation/toggle-overlay", () => {
      setOverlayEnabled(_temp2$1);
    })].filter(Boolean);
    return () => unsubs.forEach(_temp3);
  }, t3 = [comlink], $[1] = comlink, $[2] = t2, $[3] = t3) : (t2 = $[2], t3 = $[3]), useEffect(t2, t3), usePerspectiveSync(comlink, dispatch), useReportDocuments(comlink, elements, perspective);
  let t4;
  $[4] !== rootElement ? (t4 = (x, y) => {
    rootElement && (rootElement.style.setProperty("--drag-preview-x", `${x}px`), rootElement.style.setProperty("--drag-preview-y", `${y - window.scrollY}px`));
  }, $[4] = rootElement, $[5] = t4) : t4 = $[5];
  const updateDragPreviewCustomProps = t4;
  let t5, t6;
  $[6] === Symbol.for("react.memo_cache_sentinel") ? (t5 = () => {
    const handleClick = _temp4, handleKeyUp = (e) => {
      isAltKey(e) && setOverlayEnabled(_temp5);
    }, handleKeydown = (e_0) => {
      isAltKey(e_0) && setOverlayEnabled(_temp6), isHotkey(["mod", "\\"], e_0) && setOverlayEnabled(_temp7);
    };
    return window.addEventListener("click", handleClick), window.addEventListener("keydown", handleKeydown), window.addEventListener("keyup", handleKeyUp), () => {
      window.removeEventListener("click", handleClick), window.removeEventListener("keydown", handleKeydown), window.removeEventListener("keyup", handleKeyUp);
    };
  }, t6 = [setOverlayEnabled], $[6] = t5, $[7] = t6) : (t5 = $[6], t6 = $[7]), useEffect(t5, t6);
  const [overlaysFlash, setOverlaysFlash] = useState(!1), [fadingOut, setFadingOut] = useState(!1), fadeOutTimeoutRef = useRef(void 0);
  let t7, t8;
  $[8] !== overlayEnabled ? (t7 = () => {
    if (overlayEnabled)
      return raf2(() => {
        setOverlaysFlash(!0), raf2(() => {
          setFadingOut(!0), fadeOutTimeoutRef.current = setTimeout(() => {
            setFadingOut(!1), setOverlaysFlash(!1);
          }, 1500);
        });
      });
    fadeOutTimeoutRef.current && (clearTimeout(fadeOutTimeoutRef.current), setOverlaysFlash(!1), setFadingOut(!1));
  }, t8 = [overlayEnabled], $[8] = overlayEnabled, $[9] = t7, $[10] = t8) : (t7 = $[9], t8 = $[10]), useEffect(t7, t8);
  let t9, t10;
  $[11] !== elements ? (t10 = elements.flatMap(_temp8), $[11] = elements, $[12] = t10) : t10 = $[12], t9 = t10;
  const documentIds = t9;
  let t11;
  $[13] === Symbol.for("react.memo_cache_sentinel") ? (t11 = () => {
    dispatch({
      type: "overlay/blur"
    });
  }, $[13] = t11) : t11 = $[13];
  const closeContextMenu = t11, optimisticActorReady = useOptimisticActorReady();
  let t12;
  t12 = optimisticActorReady ? _componentResolver : void 0;
  const componentResolver = t12;
  let t13;
  bb0: {
    if ((inFrame || inPopUp) && comlinkStatus !== "connected" || isDragging) {
      let t143;
      $[14] === Symbol.for("react.memo_cache_sentinel") ? (t143 = [], $[14] = t143) : t143 = $[14], t13 = t143;
      break bb0;
    }
    let t142;
    $[15] !== componentResolver || $[16] !== dragMinimapTransition || $[17] !== dragShowMinimap || $[18] !== elements || $[19] !== inFrame || $[20] !== isDragging || $[21] !== optimisticActorReady || $[22] !== wasMaybeCollapsed ? (t142 = elements.filter(_temp9).map((t152) => {
      const {
        id,
        element: element_0,
        focused,
        hovered,
        rect,
        sanity,
        dragDisabled
      } = t152, draggable = !dragDisabled && !!element_0.getAttribute("data-sanity") && optimisticActorReady && elements.some((e_2) => "id" in e_2.sanity && "id" in sanity ? sanityNodesExistInSameArray(e_2.sanity, sanity) && e_2.sanity.path !== sanity.path : !1);
      return /* @__PURE__ */ jsx(ElementOverlay, { componentResolver, element: element_0, enableScrollIntoView: !isDragging && !dragMinimapTransition && !dragShowMinimap, focused, hovered, node: sanity, rect, showActions: !inFrame, draggable, isDragging: isDragging || dragMinimapTransition, wasMaybeCollapsed: focused && wasMaybeCollapsed }, id);
    }), $[15] = componentResolver, $[16] = dragMinimapTransition, $[17] = dragShowMinimap, $[18] = elements, $[19] = inFrame, $[20] = isDragging, $[21] = optimisticActorReady, $[22] = wasMaybeCollapsed, $[23] = t142) : t142 = $[23], t13 = t142;
  }
  const elementsToRender = t13, t14 = prefersDark ? "dark" : "light", t15 = fadingOut ? "" : void 0, t16 = overlaysFlash ? "" : void 0;
  let t17;
  $[24] !== documentIds || $[25] !== perspective ? (t17 = /* @__PURE__ */ jsx(DocumentReporter, { documentIds, perspective }), $[24] = documentIds, $[25] = perspective, $[26] = t17) : t17 = $[26];
  let t18;
  $[27] !== comlink || $[28] !== inFrame || $[29] !== inPopUp || $[30] !== overlayEnabled || $[31] !== rootElement || $[32] !== updateDragPreviewCustomProps ? (t18 = /* @__PURE__ */ jsx(OverlaysController, { comlink, dispatch, inFrame, inPopUp, onDrag: updateDragPreviewCustomProps, overlayEnabled, rootElement }), $[27] = comlink, $[28] = inFrame, $[29] = inPopUp, $[30] = overlayEnabled, $[31] = rootElement, $[32] = updateDragPreviewCustomProps, $[33] = t18) : t18 = $[33];
  let t19;
  $[34] !== contextMenu ? (t19 = contextMenu && /* @__PURE__ */ jsx(ContextMenu, { ...contextMenu, onDismiss: closeContextMenu }), $[34] = contextMenu, $[35] = t19) : t19 = $[35];
  let t20;
  $[36] !== dragGroupRect || $[37] !== dragInsertPosition || $[38] !== dragMinimapTransition || $[39] !== dragShowMinimapPrompt || $[40] !== isDragging ? (t20 = isDragging && !dragMinimapTransition && /* @__PURE__ */ jsxs(Fragment, { children: [
    dragInsertPosition && /* @__PURE__ */ jsx(OverlayDragInsertMarker, { dragInsertPosition }),
    dragShowMinimapPrompt && /* @__PURE__ */ jsx(OverlayMinimapPrompt, {}),
    dragGroupRect && /* @__PURE__ */ jsx(OverlayDragGroupRect, { dragGroupRect })
  ] }), $[36] = dragGroupRect, $[37] = dragInsertPosition, $[38] = dragMinimapTransition, $[39] = dragShowMinimapPrompt, $[40] = isDragging, $[41] = t20) : t20 = $[41];
  let t21;
  $[42] !== dragSkeleton || $[43] !== isDragging ? (t21 = isDragging && dragSkeleton && /* @__PURE__ */ jsx(OverlayDragPreview, { skeleton: dragSkeleton }), $[42] = dragSkeleton, $[43] = isDragging, $[44] = t21) : t21 = $[44];
  let t22;
  $[45] !== elementsToRender || $[46] !== t15 || $[47] !== t16 || $[48] !== t17 || $[49] !== t18 || $[50] !== t19 || $[51] !== t20 || $[52] !== t21 || $[53] !== zIndex ? (t22 = /* @__PURE__ */ jsxs(Root, { "data-fading-out": t15, "data-overlays": t16, ref: setRootElement, $zIndex: zIndex, children: [
    t17,
    t18,
    t19,
    elementsToRender,
    t20,
    t21
  ] }), $[45] = elementsToRender, $[46] = t15, $[47] = t16, $[48] = t17, $[49] = t18, $[50] = t19, $[51] = t20, $[52] = t21, $[53] = zIndex, $[54] = t22) : t22 = $[54];
  let t23;
  $[55] !== comlink || $[56] !== t22 ? (t23 = /* @__PURE__ */ jsx(SharedStateProvider, { comlink, children: t22 }), $[55] = comlink, $[56] = t22, $[57] = t23) : t23 = $[57];
  let t24;
  $[58] !== comlink || $[59] !== t23 ? (t24 = /* @__PURE__ */ jsx(PreviewSnapshotsProvider, { comlink, children: t23 }), $[58] = comlink, $[59] = t23, $[60] = t24) : t24 = $[60];
  let t25;
  $[61] !== comlink || $[62] !== elements || $[63] !== t24 ? (t25 = /* @__PURE__ */ jsx(SchemaProvider, { comlink, elements, children: t24 }), $[61] = comlink, $[62] = elements, $[63] = t24, $[64] = t25) : t25 = $[64];
  let t26;
  $[65] !== rootElement || $[66] !== t25 ? (t26 = /* @__PURE__ */ jsx(LayerProvider, { children: /* @__PURE__ */ jsx(PortalProvider, { element: rootElement, children: t25 }) }), $[65] = rootElement, $[66] = t25, $[67] = t26) : t26 = $[67];
  let t27;
  $[68] !== t14 || $[69] !== t26 ? (t27 = /* @__PURE__ */ jsx(ThemeProvider, { scheme: t14, theme: studioTheme, tone: "transparent", children: t26 }), $[68] = t14, $[69] = t26, $[70] = t27) : t27 = $[70];
  let t28;
  return $[71] !== comlink || $[72] !== t27 ? (t28 = /* @__PURE__ */ jsx(TelemetryProvider, { comlink, children: t27 }), $[71] = comlink, $[72] = t27, $[73] = t28) : t28 = $[73], t28;
}, IS_MAC = typeof window < "u" && /Mac|iPod|iPhone|iPad/.test(window.navigator.platform), MODIFIERS = {
  alt: "altKey",
  ctrl: "ctrlKey",
  mod: IS_MAC ? "metaKey" : "ctrlKey",
  shift: "shiftKey"
};
function isHotkey(keys, event) {
  return keys.every((key) => MODIFIERS[key] ? event[MODIFIERS[key]] : event.key === key.toUpperCase());
}
function isAltKey(event) {
  return event.key === "Alt";
}
function _temp$1(acc, prevId) {
  return acc.filter((id) => id !== prevId);
}
function _temp2$1(enabled) {
  return !enabled;
}
function _temp3(unsub) {
  return unsub();
}
function _temp4(event) {
  const target = event.target;
  if ((isHTMLAnchorElement(target) || isHTMLElement(target) && target.closest("a")) && event.altKey) {
    event.preventDefault(), event.stopPropagation();
    const newEvent = new MouseEvent(event.type, {
      ...event,
      altKey: !1,
      bubbles: !0,
      cancelable: !0
    });
    event.target?.dispatchEvent(newEvent);
  }
}
function _temp5(enabled_0) {
  return !enabled_0;
}
function _temp6(enabled_1) {
  return !enabled_1;
}
function _temp7(enabled_2) {
  return !enabled_2;
}
function _temp8(element) {
  return "id" in element.sanity ? [element.sanity.id] : [];
}
function _temp9(e_1) {
  return e_1.activated || e_1.focused;
}
const Refresh = (props) => {
  const $ = c(4), {
    comlink,
    refresh
  } = props, manualRefreshRef = useRef(0), mutationRefreshRef = useRef(0);
  let t0, t1;
  return $[0] !== comlink || $[1] !== refresh ? (t0 = () => comlink.on("presentation/refresh", (data) => {
    if (data.source === "manual") {
      clearTimeout(manualRefreshRef.current);
      const promise = refresh(data);
      if (promise === !1)
        return;
      comlink.post("visual-editing/refreshing", data);
      let timedOut;
      timedOut = !1, manualRefreshRef.current = window.setTimeout(() => {
        comlink.post("visual-editing/refreshed", data), timedOut = !0;
      }, 3e3), promise?.finally?.(() => {
        timedOut || (clearTimeout(manualRefreshRef.current), comlink.post("visual-editing/refreshed", data));
      });
    } else if (data.source === "mutation") {
      clearTimeout(mutationRefreshRef.current);
      const promise_0 = refresh(data);
      if (promise_0 === !1)
        return;
      comlink.post("visual-editing/refreshing", data), mutationRefreshRef.current = window.setTimeout(() => {
        const promise_1 = refresh(data);
        promise_1 !== !1 && (comlink.post("visual-editing/refreshing", data), promise_1?.finally?.(() => {
          comlink.post("visual-editing/refreshed", data);
        }) || comlink.post("visual-editing/refreshed", data));
      }, 1e3), promise_0?.finally?.(() => {
        comlink.post("visual-editing/refreshed", data);
      }) || comlink.post("visual-editing/refreshed", data);
    }
  }), t1 = [comlink, refresh], $[0] = comlink, $[1] = refresh, $[2] = t0, $[3] = t1) : (t0 = $[2], t1 = $[3]), useEffect(t0, t1), null;
};
function useComlink(t0) {
  const $ = c(6), active = t0 === void 0 ? !0 : t0, [node, setNode] = useState(), [status, setStatus] = useState("idle");
  let t1, t2;
  $[0] !== active ? (t1 = () => {
    if (!active)
      return;
    const instance = createNode({
      name: "visual-editing",
      connectTo: "presentation"
    }, createNodeMachine().provide({
      actors: createCompatibilityActors()
    })), unsub = instance.onStatus(() => setStatus("connected"), "connected");
    setNode(instance);
    const stop = instance.start();
    return () => {
      unsub(), stop(), setNode(void 0);
    };
  }, t2 = [active], $[0] = active, $[1] = t1, $[2] = t2) : (t1 = $[1], t2 = $[2]), useEffect(t1, t2);
  let t3;
  return $[3] !== node || $[4] !== status ? (t3 = [node, status], $[3] = node, $[4] = status, $[5] = t3) : t3 = $[5], t3;
}
function createSharedListener(comlink) {
  const incomingConnection$ = new ReplaySubject(1), incomingMutations$ = new Subject();
  return comlink.fetch("visual-editing/snapshot-welcome", void 0, {
    suppressWarnings: !0
  }).then((data) => {
    incomingConnection$.next(data.event);
  }).catch(() => {
  }), comlink.on("presentation/snapshot-event", (data) => {
    data.event.type === "reconnect" && incomingConnection$.next(data.event), data.event.type === "mutation" && incomingMutations$.next(data.event);
  }), merge(incomingConnection$, incomingMutations$);
}
function useDatasetMutator(comlink) {
  const $ = c(3);
  let t0, t1;
  $[0] !== comlink ? (t0 = () => {
    if (!comlink)
      return;
    const listener = createSharedListener(comlink), datasetMutator = createDatasetMutator(comlink), mutator = createActor(datasetMutator, {
      input: {
        client: {
          withConfig: _temp
        },
        sharedListener: listener
      }
    });
    mutator.start();
    const featuresFetch = new AbortController();
    return comlink.fetch("visual-editing/features", void 0, {
      signal: featuresFetch.signal,
      suppressWarnings: !0
    }).then((data) => {
      data.features.optimistic && setActor(mutator);
    }).catch(_temp2), () => {
      mutator.stop(), featuresFetch.abort();
    };
  }, t1 = [comlink], $[0] = comlink, $[1] = t0, $[2] = t1) : (t0 = $[1], t1 = $[2]), useEffect(t0, t1);
}
function _temp2() {
  console.warn("[@sanity/visual-editing] Package version mismatch detected: Please update your Sanity studio to prevent potential compatibility issues.");
}
function _temp() {
}
const VisualEditing = (props) => {
  const $ = c(22), {
    components,
    history,
    portal: t0,
    refresh,
    zIndex
  } = props, portal = t0 === void 0 ? !0 : t0, [inFrame, setInFrame] = useState(null), [inPopUp, setInPopUp] = useState(null);
  let t1, t2;
  $[0] === Symbol.for("react.memo_cache_sentinel") ? (t1 = () => {
    setInFrame(isMaybePreviewIframe()), setInPopUp(isMaybePreviewWindow());
  }, t2 = [], $[0] = t1, $[1] = t2) : (t1 = $[0], t2 = $[1]), useEffect(t1, t2);
  const [portalElement, setPortalElement] = useState(null);
  let t3, t4;
  $[2] !== portal ? (t3 = () => {
    if (portal === !1)
      return;
    const node = document.createElement("sanity-visual-editing");
    return document.documentElement.appendChild(node), setPortalElement(node), () => {
      setPortalElement(null), document.documentElement.contains(node) && document.documentElement.removeChild(node);
    };
  }, t4 = [portal], $[2] = portal, $[3] = t3, $[4] = t4) : (t3 = $[3], t4 = $[4]), useEffect(t3, t4);
  const [comlink, comlinkStatus] = useComlink(inFrame === !0 || inPopUp === !0);
  useDatasetMutator(comlinkStatus === "connected" ? comlink : void 0);
  let t5;
  $[5] !== comlink || $[6] !== comlinkStatus || $[7] !== components || $[8] !== inFrame || $[9] !== inPopUp || $[10] !== zIndex ? (t5 = inFrame !== null && inPopUp !== null && /* @__PURE__ */ jsx(Overlays, { comlink, comlinkStatus, componentResolver: components, inFrame, inPopUp, zIndex }), $[5] = comlink, $[6] = comlinkStatus, $[7] = components, $[8] = inFrame, $[9] = inPopUp, $[10] = zIndex, $[11] = t5) : t5 = $[11];
  let t6;
  $[12] !== comlink || $[13] !== history || $[14] !== refresh ? (t6 = comlink && /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(History, { comlink, history }),
    /* @__PURE__ */ jsx(Meta, { comlink }),
    refresh && /* @__PURE__ */ jsx(Refresh, { comlink, refresh })
  ] }), $[12] = comlink, $[13] = history, $[14] = refresh, $[15] = t6) : t6 = $[15];
  let t7;
  $[16] !== t5 || $[17] !== t6 ? (t7 = /* @__PURE__ */ jsxs(Fragment, { children: [
    t5,
    t6
  ] }), $[16] = t5, $[17] = t6, $[18] = t7) : t7 = $[18];
  const children = t7;
  if (portal === !1 || !portalElement)
    return children;
  let t8;
  return $[19] !== children || $[20] !== portalElement ? (t8 = createPortal(children, portalElement), $[19] = children, $[20] = portalElement, $[21] = t8) : t8 = $[21], t8;
};
VisualEditing.displayName = "VisualEditing";
export {
  VisualEditing,
  createDataAttribute,
  createDatasetMutator,
  createDocumentMutator,
  emptyActor,
  useDocuments,
  useOptimistic,
  useOptimisticActor
};
//# sourceMappingURL=index.js.map
